import type { MouseEvent } from 'react'
import { legacyCssClasses, legacyHookAttrs, type LegacyCssTokenName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { applyMailboxMode, pushMailboxEditorUrl, type MailboxRouteMode } from '../mailboxRoutes'
import { MailboxIcon } from './MailboxIcons'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'

type Folder = {
  label: string
  icon: Parameters<typeof MailboxIcon>[0]['name']
  contract: LegacyCssTokenName
  switcherId: number
  mode: MailboxRouteMode
  count?: number
}

function activeMode(mode: string) {
  if (mode === 'answers') return 'answers'
  if (mode === 'submsges') return 'submsges'
  return mode
}

function FolderButton({ folder, currentMode, collapsed }: { folder: Folder; currentMode: string; collapsed: boolean }) {
  const active = activeMode(currentMode) === folder.mode

  function handleClick(event: MouseEvent<HTMLElement>) {
    event.preventDefault()
    applyMailboxMode(folder.mode, event.currentTarget, { updateUrl: true })
  }

  return (
    <li
      className={`${legacyCssClasses(folder.contract)} md-mailbox-folder group relative flex h-10 cursor-pointer items-center gap-3 rounded-md px-3 text-[13px] transition-colors ${collapsed ? 'justify-center px-0' : ''} max-[760px]:justify-center max-[760px]:px-0 ${active ? 'md-sidebar__nav-item--selected bg-[#e7f0ff] font-semibold text-[#1556a8] shadow-[inset_3px_0_0_#2563eb]' : 'text-[#3b4652] hover:bg-[#eef3f7]'}`}
      title={folder.label}
      onClick={handleClick}
    >
      <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-md ${active ? 'bg-[#2563eb] text-white' : 'bg-[#e2e8ef] text-[#526272] group-hover:bg-white'}`}>
        <MailboxIcon name={folder.icon} className="h-4 w-4" />
      </span>
      <span className={`min-w-0 flex-1 truncate ${collapsed ? 'hidden' : ''} max-[760px]:hidden`}>{folder.label}</span>
      {folder.count ? (
        <span className={`${collapsed ? 'absolute right-1 top-1 h-4 min-w-4 rounded-full bg-[#2563eb] px-1 text-center text-[9px] leading-4 text-white' : 'rounded-full bg-[#e1e7ee] px-2 py-0.5 text-[11px] tabular-nums text-[#536170]'} max-[760px]:absolute max-[760px]:right-1 max-[760px]:top-1 max-[760px]:h-4 max-[760px]:min-w-4 max-[760px]:rounded-full max-[760px]:bg-[#2563eb] max-[760px]:px-1 max-[760px]:text-center max-[760px]:text-[9px] max-[760px]:leading-4 max-[760px]:text-white`}>
          {folder.count}
        </span>
      ) : null}
    </li>
  )
}

export function MailboxMenu({
  isEditorOpen = false,
  collapsed = false,
  onToggleCollapsed,
}: {
  isEditorOpen?: boolean
  collapsed?: boolean
  onToggleCollapsed?: () => void
}) {
  // Menu semantics (counts, gating, loading) come from the shared headless
  // menu model; the mailbox owns only labels, icons, layout, and skin.
  const menu = useDeskMenu()
  const mode = menu.mode
  // LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked template.
  // Keep sidebar.root (+ sidebar.nav.* contracts) MOUNTED but hidden.
  const locked = !menu.access.showUnlocked
  // MAILBOX EMPTY POLICY: collection folders remain visible at count zero.
  // Structural availability is still authoritative (an answer desk has no
  // Messages branch, regardless of its product surface).
  const folders: Folder[] = [
    { label: 'Inbox', icon: 'inbox', contract: 'sidebar.nav.answers', switcherId: 9, mode: 'answers', count: menu.tabByMode.answers.count },
  ]
  if (menu.tabByMode.submsges.available) {
    folders.push({ label: 'Messages', icon: 'message', contract: 'sidebar.nav.messages', switcherId: 10, mode: 'submsges', count: menu.tabByMode.submsges.count })
  }
  folders.push(
    { label: 'Attachments', icon: 'attachment', contract: 'sidebar.nav.attachments', switcherId: 8, mode: 'attachments', count: menu.tabByMode.attachments.count },
  )

  // FAVS-IDENTITY: Starred shows for owner state or a logged visitor (their
  // own bucket); never for anonymous viewers of an edit-locked desk.
  if (menu.can.favs) {
    folders.push({ label: 'Starred', icon: 'star', contract: 'sidebar.nav.bookmarks', switcherId: 6, mode: 'favs', count: menu.tabByMode.favs.count })
  }

  if (menu.access.editUnlocked) {
    folders.push(
      { label: 'Drafts', icon: 'edit', contract: 'sidebar.nav.drafts', switcherId: 13, mode: 'drafts', count: menu.tabByMode.drafts.count },
      { label: 'Deleted', icon: 'trash', contract: 'sidebar.nav.trash', switcherId: 5, mode: 'trash', count: menu.tabByMode.trash.count },
    )
  }

  function handleHome(event: MouseEvent<HTMLElement>) {
    event.preventDefault()
    applyMailboxMode('msg', event.currentTarget, { updateUrl: true })
  }

  function handleNewMessage(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    dispatchEditorAction({ action: 'open-new-message' })
    pushMailboxEditorUrl('new_msg')
  }

  return (
    <aside {...legacyHookAttrs('sidebar.root', `md-mailbox-menu flex min-h-0 ${collapsed ? 'w-[64px] flex-[0_0_64px]' : 'w-[232px] flex-[0_0_232px]'} flex-col border-r border-[#d4dde6] bg-[#f6f8fb] text-[#1f2933] shadow-[1px_0_0_rgba(23,33,43,0.03)] transition-[width,flex-basis] duration-150 max-[760px]:!w-[64px] max-[760px]:!flex-[0_0_64px]${locked ? ' hidden' : ''}`)} style={locked ? { display: 'none' } : undefined}>
      <div className={`flex h-14 items-center gap-2 border-b border-[#d9e1e8] px-3 ${collapsed ? 'justify-center' : ''} max-[760px]:justify-center max-[760px]:px-2`}>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md bg-[#2563eb] text-white shadow-sm hover:bg-[#1d4ed8]"
          onClick={collapsed ? onToggleCollapsed : undefined}
          title={collapsed ? 'Expand menu' : 'Msgdesk Mail'}
        >
          <MailboxIcon name="mail" className="h-4 w-4" />
        </button>
        <span className={`min-w-0 flex-1 truncate text-[16px] font-semibold ${collapsed ? 'hidden' : ''} max-[760px]:hidden`}>Msgdesk Mail</span>
        <button
          type="button"
          className={`flex h-8 w-8 flex-none items-center justify-center rounded-md border border-[#d4dde6] bg-white text-[#526272] hover:bg-[#edf2f6] ${collapsed ? 'hidden' : ''} max-[760px]:hidden`}
          onClick={onToggleCollapsed}
          title="Collapse menu"
        >
          <MailboxIcon name="chevronLeft" className="h-4 w-4" />
        </button>
      </div>

      <div className="p-3 max-[760px]:px-2">
        <button type="button" className={`flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#2563eb] px-3 text-[13px] font-semibold text-white shadow-sm hover:bg-[#1d4ed8] ${collapsed ? 'px-0' : ''}`} onClick={handleNewMessage} title="New message">
          <MailboxIcon name="plus" className="h-4 w-4" />
          <span className={`${collapsed ? 'hidden' : ''} max-[760px]:hidden`}>New message</span>
        </button>
      </div>

      {/* PARENTS-MENU: shared skeleton leaf, mailbox skin. Mounted OUTSIDE the
          scrolling nav so the dropdown overlays instead of scrolling it. */}
      <ParentsMenu skin={{
        root: `relative mx-2 mb-1 ${collapsed ? 'hidden' : ''} max-[760px]:hidden`,
        trigger: 'flex h-10 w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent px-3 text-left text-[13px] text-[#3b4652] hover:bg-[#eef3f7]',
        triggerOpen: 'bg-[#eef3f7]',
        count: 'rounded-full bg-[#e1e7ee] px-2 py-0.5 text-[11px] tabular-nums text-[#536170]',
        list: 'absolute left-0 right-0 top-full z-20 flex flex-col gap-0.5 rounded-md border border-[#d4dde6] bg-white p-2 shadow-lg',
        link: 'truncate rounded px-2 py-1 text-[12px] text-[#2563eb] no-underline hover:bg-[#eef3f7] hover:underline',
        homeLink: 'mt-0.5 truncate rounded border-t border-[#d9e1e8] px-2 py-1 pt-1.5 text-[12px] text-[#74808c] no-underline hover:bg-[#eef3f7]',
      }} />

      <nav className="min-h-0 flex-1 overflow-y-auto px-2">
        <button
          className={`${legacyCssClasses('sidebar.thisMessage')} mb-2 flex h-10 w-full items-center gap-3 rounded-md px-3 text-left text-[13px] ${collapsed ? 'justify-center px-0' : ''} ${mode === 'msg' && !isEditorOpen ? 'bg-[#e7f3e9] font-semibold text-[#236b3a] shadow-[inset_3px_0_0_#2f8a49]' : 'text-[#3b4652] hover:bg-[#eef3f7]'}`}
          type="button"
          title="Reading pane"
          onClick={handleHome}
        >
          <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-[#d9eadf] text-[#236b3a]">
            <MailboxIcon name="archive" className="h-4 w-4" />
          </span>
          <span className={`truncate ${collapsed ? 'hidden' : ''} max-[760px]:hidden`}>Reading pane</span>
        </button>
        <div className={`mb-2 px-3 text-[10px] font-semibold uppercase text-[#7b8794] ${collapsed ? 'hidden' : ''} max-[760px]:hidden`}>Folders</div>
        <ul className="space-y-1 p-0">
          {folders.map(folder => <FolderButton key={folder.mode} folder={folder} currentMode={mode} collapsed={collapsed} />)}
        </ul>
      </nav>

      {collapsed ? (
        <button
          type="button"
          className="m-3 flex h-8 items-center justify-center rounded-md border border-[#d4dde6] bg-white text-[#526272] hover:bg-[#edf2f6] max-[760px]:hidden"
          onClick={onToggleCollapsed}
          title="Expand menu"
        >
          <MailboxIcon name="chevronRight" className="h-4 w-4" />
        </button>
      ) : (
        <div className="border-t border-[#d9e1e8] p-3 text-[11px] text-[#74808c] max-[760px]:hidden">
          {/* POSSESSION-MODEL: the sidebar shows the desk's own state; the
              logged identity moved to the page top-right (MailboxMsgPage). */}
          <DeskStateBadge skin={{
            root: 'mb-2 flex items-center whitespace-nowrap text-[11px] font-medium',
            possessedOpen: 'text-emerald-700',
            possessedPin: 'text-emerald-700',
            locked: 'text-[#74808c]',
            label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2',
            form: 'ml-1.5 inline-flex items-center gap-1',
            input: 'h-[26px] w-[100px] rounded-md border border-[#d4dde6] bg-white px-2 text-[12px] text-[#17212b] outline-none',
            button: 'h-[26px] cursor-pointer rounded-md px-2 text-[12px] text-[#2563eb] hover:bg-[#edf2f6]',
          }} />
          {menu.subsLoading ? `Loading ${menu.subsLoading.count} items` : 'Connected'}
        </div>
      )}
    </aside>
  )
}
